import eventClickCategoryNews from 'ui/lib/gtm/eventClickCategoryNews';
import Link from 'next/link';
import { Fragment, useRef } from 'react';
import { useRouter } from '../../../hooks/useCustomRouter';
import useImpressionProductKnowledgeList from '../../../hooks/emarsys/product-knowledge/useImpressionProductKnowledgeList';
import eventClickProductKnowledgeList from '../../../helpers/emarsys/product-knowledge/eventClickProductKnowledgeList';
import eventClickNewsList from '../../../helpers/emarsys/news/eventClickNewsList';
import eventClickTestDriveList from '../../../helpers/emarsys/test-drive/eventClickTestDriveList';
import { FEATURE } from '../../../constants/emarsys/feature';
import { SECTION_NAME_TEST_DRIVE_LIST } from '../../../constants/emarsys/test-drive';
import { IBreadcrumbs } from '../../molecules/Breadcrumbs';

interface ITabsProps {
  tabs: {
    name?: string;
    path?: string;
    selected?: boolean;
    query?: string | { [key: string]: string | number | boolean };
    count?: number;
    testId?: string;
  }[];
  showBadge?: boolean;
  isLowerCase?: boolean;
  children?: React.ReactNode;
  wrapperClassName?: string;
  tabClassname?: string;
  itemClassName?: string;
  from?: string;
  enableRedirect?: boolean;
  handleTabsClick?: (name: string) => void;
  selectedName?: string;
  additionalTab?: React.ReactNode;
  basePath?: string;
  showAsHeading2?: boolean;
  eachTabClassName?: string;
  forAccessories?: boolean;
  feature?: string;
  emarsysData?: {
    title?: string;
    category?: string;
    page_number?: number;
    menu_cat_lvl1?: string;
    menu_cat_lvl2?: string;
  };
}

const Tabs = ({
  tabs,
  children,
  basePath,
  showBadge = false,
  showAsHeading2 = false,
  wrapperClassName = '',
  tabClassname = 'top-[60px] lg:top-[173px] xl:top-[171px]',
  itemClassName = '',
  isLowerCase = true,
  from = '',
  enableRedirect = true,
  handleTabsClick = () => {},
  selectedName = '',
  additionalTab = <Fragment />,
  eachTabClassName,
  forAccessories = false,
  feature = '',
  emarsysData = {},
}: ITabsProps) => {
  const router = useRouter();
  const BASEPATH_URL = basePath || process.env.NEXT_PUBLIC_BASEPATH_URL;

  const impressionRef = useRef(null);

  const category = from === 'NCP' ? router?.query?.q || '' : router?.query?.kategori || '';
  const handlePath = (tab) => {
    return isLowerCase
      ? `${BASEPATH_URL}${tab?.path?.toLowerCase()}`
      : `${BASEPATH_URL}${tab?.path}`;
  };

  if (feature === 'LISTING_PRODUCT_KNOWLEDGE') {
    useImpressionProductKnowledgeList(
      impressionRef,
      emarsysData.title,
      emarsysData.category,
      emarsysData.page_number,
      'page_category'
    );
  }

  return (
    <div className={wrapperClassName}>
      <div className={`flex justify-between items-center sticky z-20 bg-white ${tabClassname}`}>
        <div className="flex items-center justify-start w-full border-b-[1px] bg-white overflow-x-auto no-scrollbar">
          {tabs?.map((tab, index) => (
            <div key={index} className={eachTabClassName}>
              {enableRedirect ? (
                <Link
                  id={`tab-${tab?.name}`}
                  key={index}
                  className={`inline-block text-xs uppercase text-reliableBlack whitespace-nowrap py-3.5 px-4 lg:text-base lg:py-4 lg:px-5 border-b-4 lg:border-b-[3px] [@media(hover:hover)]:hover:border-supportiveRed active:border-supportiveRed ${itemClassName} ${
                    (from === 'NCP' &&
                      (category as string)?.split(':')?.pop()?.toLowerCase() ===
                        tab?.name?.toLowerCase()) ||
                    (from !== 'NCP' &&
                      (category as string)?.trim()?.split('-')?.join(' ') ===
                        tab?.name?.toLowerCase()) ||
                    tab?.selected === true
                      ? 'font-bold border-supportiveRed'
                      : 'border-transparent'
                  }`}
                  href={
                    !forAccessories
                      ? {
                          pathname: isLowerCase
                            ? `${BASEPATH_URL}${tab?.path?.toLowerCase()}`
                            : `${BASEPATH_URL}${tab?.path}`,
                          query:
                            from === 'NCP' && category
                              ? {
                                  q: category,
                                  ...(router?.query?.sort ? { sort: router?.query?.sort } : {}),
                                  ...(router?.query?.priceFilter
                                    ? { priceFilter: router?.query?.priceFilter }
                                    : {}),
                                  ...(router?.query?.city ? { city: router?.query?.city } : {}),
                                }
                              : from === 'PK' ||
                                  from === 'NT' ||
                                  from === 'coupon' ||
                                  from === 'package'
                                ? tab?.query
                                : null,
                        }
                      : handlePath(tab)
                  }
                  onClick={() => {
                    eventClickCategoryNews(tab.name, from);
                    if (feature === 'LISTING_PRODUCT_KNOWLEDGE') {
                      eventClickProductKnowledgeList(
                        emarsysData.title,
                        tab.name,
                        emarsysData.page_number,
                        'page_category',
                        tab.name
                      );
                    }
                    if (feature === FEATURE.TEST_DRIVE_LISTING_PAGE) {
                      eventClickTestDriveList(
                        emarsysData.title,
                        emarsysData.category,
                        SECTION_NAME_TEST_DRIVE_LIST.LISTING_PAGE,
                        tab.name,
                        emarsysData?.menu_cat_lvl1,
                        {
                          menu_cat_lvl2: emarsysData?.menu_cat_lvl2,
                        }
                      );
                    } else {
                      eventClickNewsList(
                        emarsysData.title,
                        emarsysData.category,
                        emarsysData.page_number,
                        'page_category',
                        tab.name
                      );
                    }
                  }}
                  onTouchEnd={(event) => {
                    const target = event.currentTarget as HTMLElement;
                    target.classList.remove('!border-supportiveRed');
                  }}
                  onTouchMove={(event) => {
                    const target = event.currentTarget as HTMLElement;
                    target.classList.add('!border-supportiveRed');
                  }}
                >
                  {showAsHeading2 ? <h2>{tab.name}</h2> : tab.name}
                </Link>
              ) : (
                <div
                  key={index}
                  data-testid={tab.testId}
                  onClick={() => handleTabsClick(tab.name)}
                  className={`${itemClassName} inline-block text-xs uppercase text-reliableBlack whitespace-nowrap py-3.5 px-4 border-b-4 lg:text-base lg:py-4 lg:px-5  lg:border-b-[3px] lg:hover:border-supportiveRed cursor-pointer ${
                    selectedName === tab.name
                      ? 'font-bold border-supportiveRed'
                      : 'border-transparent'
                  }`}
                >
                  {showAsHeading2 ? (
                    <h2>{tab.name}</h2>
                  ) : (
                    <>
                      <p>{tab.name}</p>
                      {!!(
                        showBadge &&
                        selectedName === tab.name &&
                        tab.count &&
                        tab.count !== 0
                      ) && (
                        <div className="text-[10px] inline-flex items-center justify-center rounded-full bg-supportiveRed text-white w-5 h-5 font-medium leading-none">
                          {tab.count}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
          {additionalTab}
        </div>
      </div>
      <div>{children}</div>
    </div>
  );
};

export default Tabs;
